import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import { RouteTypes } from '~/routes/constants'
import DairyQuery from '~/QueryHooks/DairyQuery'
import { Dropdown, Input } from 'semantic-ui-react'
import { useDebounce } from 'use-lodash-debounce';
import { ManagerUsersQuery } from '~/QueryHooks/ManagerUsersQuery'
import { FiltersWrapper } from '../ActivityLogs/style'
import ExcelExport from '~/components/ExcelExport'
import { El } from '~/components/Layout/style'
import { useNavigate } from 'react-router'
import { StyledTab } from '~/styles/table'
import { useSearchParams } from 'react-router-dom'
import i18n from '~/components/i18n'

const filterKeys = ['search', 'agisId', 'membershipStatusId', 'type'] as const

const buildFilterQuery = (filters) => {
  const params = new URLSearchParams()
  filterKeys.forEach(key => {
    if (filters[key]) params.set(key, filters[key])
  })
  const query = params.toString()
  return query ? `&${query}` : ''
}

const getActions = (filterQuery: string) => [
  {
    popupMessage: i18n.t('Manager Profile'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_MANAGERS_SHOW}?tab=manager-details${filterQuery}`
  },
  {
    popupMessage: i18n.t('Users'),
    type: 'link',
    name: 'users',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_MANAGERS_SHOW}?tab=manager-user${filterQuery}`
  }
]

const Managers: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const getInitialFilters = () => ({
    page: 0,
    rows: 20,
    agisId: searchParams.get('agisId') || '',
    membershipStatusId: searchParams.get('membershipStatusId') || '',
    search: searchParams.get('search') || '',
    type: searchParams.get('type') || 'all_managers'
  })

  const [dataFiltered, setDataFiltered] = useState(getInitialFilters)
  const [agisId, setAgisId] = useState(searchParams.get('agisId') || '')
  const [search, setSearch] = useState(searchParams.get('search') || '')

  const debounceAgisValue = useDebounce(agisId, 300);
  const debouncedSearchValue = useDebounce(search, 300);
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const {
    isLoading: isBusyManagers,
    data: managersData,
    isFetching: isFetchingData,
    refetch: fetchManagers
  } = DairyQuery.getManagers({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    isLoading: isLoadingOptions,
    data: options,
    refetch: fetchOptions
  } = ManagerUsersQuery.getManagersIndexOptions();

  const {
    isLoading: isBusyManagersCount,
    data: managersCount,
    refetch: fetchCount
  } = DairyQuery.countManagers({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const defautlTableColumnHeaders = [
    {
      name: t('AGIS-ID Person'),
      key: 'agisId',
      sorted: 'desc'
    },
    {
      name: t('Managers name'),
      key: 'name',
      sorted: 'desc'
    },
    {
      name: t('Member Status'),
      key: 'membershipStatusName'
    },
    {
      name: t('Farmers'),
      key: 'farmers',
      type: 'manager-farmers'
    },
    {
      name: t('Street name'),
      key: 'streetName',
      type: 'street'
    },
    {
      name: t('Postleitzahl'),
      key: 'zip',
      sorted: 'asc'
    },
    {
      name: t('Ort'),
      key: 'city',
      sorted: 'asc'
    },
    {
      name: t('Actions')
    }
  ]

  const [tableColumnHeaders, setTableColumnHeaders] = useState(defautlTableColumnHeaders)

  useEffect(() => {
    if (dataFiltered) {
      fetchManagers();
      fetchCount()
    }
  }, [dataFiltered]);

  useEffect(() => {
    fetchOptions()
    setTableColumnHeaders(defautlTableColumnHeaders)
  }, [t])

  useEffect(() => {
    if (options) {
      setActiveTabIndex(options.types.findIndex(type => type.key === dataFiltered.type))
    }
  }, [options])

  useEffect(() => {
    const params = new URLSearchParams()
    filterKeys.forEach(key => {
      if (dataFiltered[key]) params.set(key, dataFiltered[key])
    })
    setSearchParams(params, { replace: true })
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, agisId: debounceAgisValue, page: 0 });
  }, [debounceAgisValue]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function renderTable() {
    return <TableComponent
      actions={getActions(buildFilterQuery(dataFiltered))}
      animated
      isFetchingData={isFetchingData}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setDataFiltered(data)
        setTableColumnHeaders(tableColumnHeaders)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderSearch() {
    return (
      <FiltersWrapper>
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('AGIS-ID Person')}
          value={agisId}
          onChange={(e, data) => setAgisId(data.value)}
        />
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('Managers name')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
        <Dropdown
          placeholder={t('Member Status')}
          noResultsMessage={t('No results found....')}
          options={options?.membershipStatuses?.map(memberStatus => ({ value: memberStatus.id, text: memberStatus.name || '' })) || []}
          onChange={(e, data) => setDataFiltered({
            ...dataFiltered,
            membershipStatusId: data.value as string
          })}
          value={dataFiltered.membershipStatusId}
          clearable
          selection
          search
          selectOnBlur={false}
        />
      </FiltersWrapper>
    );
  }

  function renderActions() {
    return <>
      <ExcelExport
        url={'managers/export'}
        fileName=''
        icon={'file excel'}
        downloadWithFileName
      />
      <El.PageHeaderActionButton
        onClick={() => navigate(RouteTypes.DAIRY_FACTORY_MANAGERS_CREATE)}
      >
        {t('Create')}
      </El.PageHeaderActionButton>
    </>
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      setActiveTabIndex(activeIndex)
      const status = options?.types?.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        type: status?.key,
        page: 0
      });
    }
  }

  function renderPanes() {
    const panes = options?.types?.map((option) => {
      const count = managersCount ? managersCount[`${option.key}`] : 0;
      return {
        menuItem: `${option.label}  (${count})`,
        render: () => renderTable()
      };
    });

    return (
      <StyledTab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  const { filtered, rows } = managersData ? managersData : { filtered: 0, rows: [] };

  return <Layout
    segmented
    title={t('Manager')}
    filters={renderSearch()}
    isBusy={isBusyManagers || isLoadingOptions || isBusyManagersCount}
    actions={renderActions()}
  >
    {renderPanes()}
  </Layout>
}
export default Managers
